import React from 'react';
import { View, Pressable } from 'react-native-web';
import CustomText from '../atoms/CustomText';
import { colors, spacing, borderRadius } from '../../utils/styling';
import { useResponsive } from '../../hooks/useResponsive';
import SvgArrow from '../icons/SvgArrow';
import ReviewCard from '../atoms/ReviewCard';
import SocialCard from '../atoms/SocialCard';

interface PrimingCardProps {
  title: string;
  content: string;
  onContinue: (socials: string[]) => void;
  continueButtonText?: string;
  type?: 'default' | 'reviews' | 'info';
}

const PrimingCard: React.FC<PrimingCardProps> = ({ 
  title, 
  content, 
  onContinue, 
  continueButtonText = "Continue",
  type = 'default'
}) => {
  const { isMobile } = useResponsive();

  // Social selection state for info type
  const [selectedSocials, setSelectedSocials] = React.useState<string[]>([]);
  const [clicked, setClicked] = React.useState<boolean>(false);
  const socials = [
    { iconPath: require('../img/instagram.png'), socialCompany: 'Instagram' },
    { iconPath: require('../img/facebook.png'), socialCompany: 'Facebook' },
    { iconPath: require('../img/linkedin.png'), socialCompany: 'LinkedIn' },
    { iconPath: require('../img/twitter.png'), socialCompany: 'X (Twitter)' },
    { iconPath: require('../img/tiktok.png'), socialCompany: 'TikTok' },
    { iconPath: require('../img/gmail.png'), socialCompany: 'Gmail' },
    { iconPath: require('../img/google.png'), socialCompany: 'Google' },
    { iconPath: require('../img/other.png'), socialCompany: 'Other' },
  ];

  const handleSocialSelect = (social: string) => {
    setSelectedSocials(prev =>
      prev.includes(social)
        ? prev.filter(s => s !== social)
        : [...prev, social]
    );
  };

  const handleContinue = async () => {
    setClicked(true);
    try {
      await onContinue(selectedSocials);
    } catch (error) {
      console.error('Error in handleContinue:', error);
    } finally {
      setClicked(false);
    }
  }

  // Different styles based on type
  const getCardStyles = () => {
    return {
      backgroundColor: colors.white,
      borderRadius: 16,
      border: '1px solid black',
      padding: isMobile ? spacing.md : spacing.xl,
      maxWidth: 1400,
      width: "100%",
      marginBottom: spacing.xxl,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    };
  };

  const getTitleStyles = () => {
    return {
      marginTop: isMobile ? spacing.md : spacing.lg, 
      fontSize: isMobile ? (type === "reviews") ? 40 : 48 : 60, 
      fontWeight: 700, 
      color: 'black', 
      fontFamily: "Nunito",
      paddingHorizontal: isMobile ? spacing.md : 0,
      textAlign: 'center',
      marginBottom: isMobile ? (type === "reviews") ? spacing.md : spacing.sm : spacing.md,
      width: isMobile ? undefined : '85%'
    };
  };

  const getContentStyles = () => {
    return {
      fontFamily: "Nunito",
      fontSize: isMobile ? 18 : 24,
      textAlign: 'center',
      color: colors.dark,
    };
  };

  const getButtonStyles = () => {
    return {
      fontFamily: "Nunito",
      fontSize: isMobile ? 18 : 24,
      fontWeight: 700,
      borderRadius: 999,
      backgroundColor: "black",
      color: "white",
      paddingVertical: isMobile ? spacing.md : spacing.lg,
      paddingHorizontal: isMobile ? spacing.lg : spacing.xl,
      cursor: 'pointer',
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.md,
      minWidth: isMobile ? 150 : 200,
      width: '90%',
      justifyContent: 'center',
      transition: 'all 0.2s',
    };
  };

  return (
    <View style={getCardStyles()}>
      <CustomText 
        variant="h2" 
        align="center" 
        style={getTitleStyles()}
      >
        {title}
      </CustomText>
      
      {type === 'reviews' ? (
        <View
          style={{
            width: isMobile ? '100%' : '95%',
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            justifyContent: 'center',
            alignItems: 'center',
            gap: isMobile ? spacing.md : spacing.lg,
            margin: isMobile ? `${spacing.md}px 0` : `${spacing.lg}px 0`,
            minHeight: isMobile ? 0 : 320,
            marginBottom: isMobile ? spacing.lg : spacing.xl,
          }}
        >
          {/* Example reviews, replace with real data as needed */}
          <ReviewCard
            photo={require('../img/brock.jpeg')}
            name="Brock Brown"
            content="What I loved most was how the quiz balanced practical advice with a deep understanding of my values. I walked away with a clear plan and a lot more confidence in my financial decisions."
          />
          {!isMobile && (
            <>
              <ReviewCard
                photo={require('../img/nina.png')}
                name="Nina Hajjar"
                content="The Money Personality quiz gave me eye-opening insights into how I view spending and savings—it's like financial therapy in five minutes!"
              />
              <ReviewCard
                photo={require('../img/ben.jpeg')}
                name="Ben Barton"
                content="I always thought I was good with money, but this quiz showed me patterns I never noticed. The results were spot-on and helped me set goals for my future."
              />
              <ReviewCard
                photo={require('../img/jeff.jpeg')}
                name="Jeff Brown"
                content="Through the Money Personality quiz, I found where I was holding myself back. The personalized tips were actionable and really helped me rethink my approach to saving and investing."
              />
              <ReviewCard
                photo={require('../img/mckayla.jpeg')}
                name="McKayla Hajjar"
                content="I've taken a lot of financial quizzes, but this one actually felt personal. The results were easy to understand and gave me steps I could use right away to improve my money habits."
              />
            </>
          )}
        </View>
      ) : type === 'info' ? (
        <View
          style={{
            width: isMobile ? '100%' : '85%',
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: isMobile ? spacing.md : spacing.lg,
            padding: isMobile ? spacing.sm : spacing.lg,
            margin: '0 auto',
            marginBottom: isMobile ? spacing.lg : spacing.xl,
          }}
        >
          {socials.map((social) => (
            <SocialCard
              key={social.socialCompany}
              iconPath={social.iconPath}
              socialCompany={social.socialCompany}
              selected={selectedSocials.includes(social.socialCompany)}
              onPress={() => handleSocialSelect(social.socialCompany)}
            />
          ))}
        </View>
      ) : (
        <View style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          width: '100%',
          marginTop: isMobile ? spacing.md : spacing.lg,
          marginBottom: isMobile ? spacing.md : spacing.lg,
          paddingHorizontal: isMobile ? spacing.xs : 0,
          justifyContent: 'center',
          gap: isMobile ? spacing.md : spacing.lg,
          alignItems: 'center',
        }}>
          <CustomText style={getContentStyles()}>
            {content}
          </CustomText>
        </View>
      )}
      <Pressable 
        style={getButtonStyles()}
        onPress={handleContinue}
      >
        <CustomText style={{
          color: "white",
          fontFamily: "Nunito",
          fontSize: type === 'info' ? (isMobile ? 16 : 20) : (isMobile ? 18 : 24),
          fontWeight: type === 'info' ? 600 : 700,
        }}>
          {clicked ? "Processing..." : continueButtonText}
        </CustomText>
        {type !== "reviews"  && (<SvgArrow direction='right' color={colors.white} width={isMobile ? 18 : 24} height={isMobile ? 18 : 24} />)}
      </Pressable>
    </View>
  );
};

export default PrimingCard; 